import { useState } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useLocalePath } from "@/hooks/useLocalePath";
import { useBlogPosts, BlogPost } from "@/hooks/useBlog";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Calendar, User, ArrowRight, BookOpen } from "lucide-react";
import logo from "@/assets/logo.png";

const LatestBlogCard = ({ post }: { post: BlogPost }) => {
  const { language } = useLanguage();
  const { getPath } = useLocalePath();
  const [imageError, setImageError] = useState(false);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <Link to={getPath(`/blog/${post.slug}`)}>
      <Card className="overflow-hidden h-full group hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
        <div className="relative h-44 overflow-hidden">
          {post.coverImage && !imageError ? (
            <img
              src={post.coverImage}
              alt={post.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              loading="lazy"
              decoding="async"
              width={400}
              height={176}
              onError={() => setImageError(true)}
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-muted to-muted/50 flex items-center justify-center">
              <div className="flex flex-col items-center gap-2 opacity-40">
                <img src={logo} alt="The Wise Compass" className="h-12 w-12" />
                <span className="text-xs font-medium text-muted-foreground">The Wise Compass</span>
              </div>
            </div>
          )}
        </div>
        <CardHeader className="pb-2">
          <div className="flex flex-wrap gap-2 mb-2">
            {post.tags.slice(0, 2).map((tag, index) => (
              <Badge key={index} variant="secondary" className="text-xs">
                {tag}
              </Badge>
            ))}
          </div>
          <h3 className="font-serif text-lg text-foreground group-hover:text-primary transition-colors line-clamp-2" style={{ textWrap: 'pretty' }}>
            {post.title}
          </h3>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-muted-foreground text-sm line-clamp-2 leading-relaxed">
            {post.excerpt}
          </p>
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1">
                <User className="h-3 w-3" />
                {post.author}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {formatDate(post.publishedDate)}
              </span>
            </div>
            <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

const LatestArticles = () => {
  const { language } = useLanguage();
  const { getPath } = useLocalePath();
  const { data: posts, isLoading, error } = useBlogPosts();

  // Show only the 3 most recent articles
  const latestPosts = posts?.slice(0, 3) ?? [];

  // Don't render the section if there's an error or no posts after loading
  if (error || (!isLoading && latestPosts.length === 0)) return null;

  return (
    <section className="py-16 md:py-24 bg-muted/25">
      <div className="container mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-primary/10 text-primary rounded-full text-sm font-medium mb-4">
            <BookOpen className="w-4 h-4" />
            {language === "fr" ? "Blog" : "Blog"}
          </div>
          <h2 className="font-serif text-3xl md:text-4xl text-foreground mb-3" style={{ textWrap: 'pretty' }}>
            {language === "fr" ? "Nos derniers articles" : "Our latest articles"}
          </h2>
          <p className="text-muted-foreground text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
            {language === "fr"
              ? "Réflexions, analyses et conseils pour une mode plus responsable"
              : "Insights, analysis and tips for more responsible fashion"}
          </p>
        </div>

        {/* Articles Grid */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[...Array(3)].map((_, i) => (
              <Card key={i} className="overflow-hidden">
                <Skeleton className="h-44 w-full" />
                <CardHeader className="space-y-2">
                  <Skeleton className="h-5 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                </CardHeader>
                <CardContent>
                  <Skeleton className="h-16 w-full" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {latestPosts.map((post) => (
              <LatestBlogCard key={post.id} post={post} />
            ))}
          </div>
        )}

        {/* CTA Button */}
        <div className="flex justify-center mt-10">
          <Link
            to={getPath("/blog")}
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-full text-sm font-medium hover:opacity-90 transition-opacity whitespace-nowrap"
          >
            {language === "fr" ? "Voir tous les articles" : "View all articles"}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default LatestArticles;
